import React, { useCallback, useMemo, useRef, useState } from 'react';

import Button from 'components/shared/Button/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import TabsTableModal from 'components/shared/Modal/TabsTableModal/TabsTableModal';

import PayerForm from '../PayerForm/PayerForm';
import { usePayerForm } from '../PayerForm/lib/usePayerForm';

import AddPayerModalHeader from './components/AddPayerModalHeader';
import AddPayerTable from './components/AddPayerTable';

const AddPayerModal = ({ isOpen, practiceId, handleClose }) => {
  const addPayerTableRef = useRef();
  const requestNewPayerFormRef = useRef();
  const formikRef = useRef();
  const selectedRowsRef = useRef([]);

  const [selectedRows, setSelectedRows] = useState([]);

  const { formik, isLoading: isFormLoading } = usePayerForm({ onSuccess: handleClose, practiceId });

  formikRef.current = formik; // Store latest formik in ref to avoid remounting PayerForm
  selectedRowsRef.current = selectedRows; // Keep ref in sync with state

  const handleCloseWithReset = () => {
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
    setSelectedRows([]);
    handleClose();
  };

  const AddPayerTableComponent = useCallback(
    () => (
      <AddPayerTable
        ref={addPayerTableRef}
        practiceId={practiceId}
        handleClose={handleClose}
        setSelectedRows={setSelectedRows}
      />
    ),
    []
  );

  const RequestNewPayerFormComponent = useCallback(
    ({ openModal }) => (
      <div className="flex h-full flex-col gap-4 px-4 pt-4">
        <PayerForm
          ref={requestNewPayerFormRef}
          practiceId={practiceId}
          isLoading={isFormLoading}
          openModal={openModal}
          formik={formikRef.current}
        />
      </div>
    ),
    [isFormLoading]
  );

  const requestNewPayerTab = useMemo(
    () => ({
      name: 'Request new payer',
      headerTitle: '',
      title: 'Request new payer',
      component: RequestNewPayerFormComponent,
      hideBackButton: true,
      continueText: ({ lockNext, loading, handleContinue }) => (
        <>
          <Button
            variant="secondary"
            color="neutral"
            text="Cancel"
            onClick={handleCloseWithReset}
            size="small"
          />
          <Button
            variant="primary"
            text="Request new payer"
            onClick={handleContinue}
            isLoading={loading || isFormLoading}
            isDisabled={lockNext}
            size="small"
          />
        </>
      ),
      onContinue: () => {
        requestNewPayerFormRef.current?.submitForm();
      }
    }),
    [RequestNewPayerFormComponent]
  );

  const tabs = useMemo(
    () => [
      {
        name: 'Add payer',
        headerTitle: 'Add payers',
        title: 'Add payer',
        component: AddPayerTableComponent,
        leftFooter: () => {
          const count = selectedRowsRef.current.length;
          return (
            <div className="text-sm text-neutral-700">{count > 0 && `${count} items selected`}</div>
          );
        },
        continueText: ({ lockNext, loading, handleContinue }) => {
          const count = selectedRowsRef.current.length;
          return (
            <Button
              variant="primary"
              text="Add payers"
              onClick={handleContinue}
              isLoading={loading}
              disabled={lockNext || count === 0}
              size="small"
            />
          );
        },
        onContinue: () => {
          addPayerTableRef.current?.handleAddPayers(selectedRowsRef.current);
        }
      }
    ],
    [AddPayerTableComponent]
  );

  const AddPayerModalHeaderWithTab = useCallback(
    ({ breadcrumb }) => (
      <AddPayerModalHeader
        breadcrumb={breadcrumb}
        requestNewPayerTab={requestNewPayerTab}
        onRequestNewPayer={() => setSelectedRows([])}
      />
    ),
    [requestNewPayerTab]
  );

  return (
    <TabsTableModal
      hasBackButton
      trimSteps
      headerClassName="px-5 py-3"
      isOpen={isOpen}
      handleClose={handleCloseWithReset}
      tabs={tabs}
      headerActions={AddPayerModalHeaderWithTab}
    />
  );
};

export default withErrorBoundary(AddPayerModal);
